import { takeLatest, put, call, select } from 'redux-saga/effects'
import { getCalendar, refreshQuotaWithDate } from '../services/scheduling'
import * as schedulingActions from '../actions/scheduling'
import {
  getUpdatedCalendarDate,
  getFilteredCalendar,
  filterCalendarsByOption,
  parseSchedulingCalendars,
  setApiPropertiesFromCallbackList,
  getPrewireMaxDate,
  shouldShowCalendarOptions,
  getDefaultCalendarOption,
  isPrewireWithAMSlots,
  isPrewireWithoutAMSlots
} from '../utils/scheduling'
import { isDateSameOrBefore } from '../utils/dates'
import * as actions from '../actions'
import * as formErrorIds from '../constants/form'
import {
  ABT_ADDRESS_CREATE_SUCCESS,
  ABT_REQUIRED,
  CALENDAR_TABS,
  GENESIS_IHAT,
  MERGEABLE_CALENDAR_PROPERTIES,
  TIME_SLOT_IDS,
  calendarTypes
} from '../constants/scheduling'
import { orderSubmitMessages } from '../constants/loading'
import { errors, success } from '../constants/content'
import { getApiErrorMessage } from '../utils/error'

function mergeCalendarDates(existingCalendar, refreshQuotaCalendar) {
  if (!existingCalendar || !refreshQuotaCalendar) {
    return refreshQuotaCalendar || existingCalendar
  }

  const updatedCalendar = { ...existingCalendar }
  
  const mergeCalendarType = calendarType => {
    if (existingCalendar[calendarType] && refreshQuotaCalendar[calendarType]) {
      const existingDates = existingCalendar[calendarType].calendarDates || []
      const apiResponseDates = 
      refreshQuotaCalendar[calendarType].calendarDates || []
      const existingDatesMap = new Map()
      existingDates.forEach(existingDate => {
        existingDatesMap.set(existingDate.date, existingDate)
      })
      const updatedDates = existingDates.map(existingDate => {
        const apiDate = 
        apiResponseDates.find(api => api.date === existingDate.date)
        
        if (apiDate) {
          return {
            ...existingDate,
            availableTimeSlots: 
            apiDate.availableTimeSlots || apiDate.timeSlots || []
          }
        } 
        return {
          ...existingDate,
          availableTimeSlots: []
        }
      })
      
      const sortedDates = 
      updatedDates.sort((a, b) => new Date(a.date) - new Date(b.date))
      updatedCalendar[calendarType] = {
        ...existingCalendar[calendarType],
        calendarDates: sortedDates
      }
    }
  }
  
  mergeCalendarType(calendarTypes.install)
  mergeCalendarType(calendarTypes.prewire) 
  
  MERGEABLE_CALENDAR_PROPERTIES.forEach(prop => {
    if (prop in refreshQuotaCalendar) {
      updatedCalendar[prop] = refreshQuotaCalendar[prop]
    }
  })
  
  return updatedCalendar
}

export function* getCalendarSaga() {
  try {
    const { fxbuyflowSessionId } = yield select(({ session }) => session)

    yield put(actions.togglePageLoading())
    yield put(actions.clearFormMessage(formErrorIds.timeSlot))
    yield put(actions.clearFormMessage(formErrorIds.abtMessage))
    yield put(actions.clearFormMessage(formErrorIds.genesisIhatMessage))

    let calendar = yield call(getCalendar, { fxbuyflowSessionId })

    if (calendar?.data?.nextApiToCall?.length === 2) {
      yield put(schedulingActions.setValidateInstallResponse(calendar.data))
      if (shouldShowCalendarOptions(calendar.data)) {
        const defaultOption =
          getDefaultCalendarOption(calendar.data.calendars)
        yield put(schedulingActions.setCalendarOption(defaultOption))
        const filteredCalendars = filterCalendarsByOption(
          calendar.data.calendars,
          defaultOption
        )
        if (defaultOption === CALENDAR_TABS.FULL_AUTOMATION) {
          const prewireWithEmptySlots = calendar.data.calendars.find(
            cal => cal.calendarType.toLowerCase() ===
              calendarTypes.prewire && cal.calendarDates?.every(date =>
                !date.availableTimeSlots || date.availableTimeSlots.length === 0
              )
          )
            
          if (prewireWithEmptySlots && !filteredCalendars.some(cal =>
            cal.calendarType.toLowerCase() === calendarTypes.prewire &&
            cal.calendarDates?.every(date => !date.availableTimeSlots
              || date.availableTimeSlots.length === 0)
          )) {
            filteredCalendars.push(prewireWithEmptySlots)
          }
        }
        setApiPropertiesFromCallbackList(
          calendar.data,
          CALENDAR_TABS.FULL_AUTOMATION
        )
        const filteredRawData = {
          ...calendar.data,
          calendars: filteredCalendars
        }
        const parsedCalendars = parseSchedulingCalendars({
          status: calendar.status,
          ...filteredRawData
        })
        yield put(actions.setGlobalWarning
        (formErrorIds.timeSlot, calendar.data.newTimeslotSelectionMessage))
        yield put(actions.setCalendar(getFilteredCalendar(parsedCalendars)))
      }
    }
    else {
      if (calendar.installMessageCodes?.includes(ABT_REQUIRED)) {
        yield put(
          actions.updatePageLoadingMessage(
            orderSubmitMessages.abtCreatingHouseKey
          )
        )
        calendar = yield call(getCalendar, { fxbuyflowSessionId })

        if (
          calendar.installMessageCodes?.includes(ABT_ADDRESS_CREATE_SUCCESS)
        ) {
          yield put(
            actions.setGlobalSuccess(
              formErrorIds.abtMessage,
              success.abtMessage
            )
          )
        } else {
          yield put(
            actions.setGlobalError(formErrorIds.abtMessage, errors.abtMessage)
          )
        }
      }

      if (calendar.installMessageCodes?.includes(GENESIS_IHAT)) {
        yield put(
          actions.setGlobalWarning(
            formErrorIds.genesisIhatMessage,
            calendar.genesisIhatMessage
          )
        )
      }

      const filteredCalendar = getFilteredCalendar(calendar)

      if (calendar.newTimeslotSelectionMessage) {
        yield put(
          actions.setGlobalWarning(
            formErrorIds.timeSlot,
            calendar.newTimeslotSelectionMessage
          )
        )
      }
      yield put(actions.setCalendar(filteredCalendar))
    }
  } catch (error) {
    yield put(
      actions.setCalendar({
        installationMessage: errors.calendarRetrievalError
      })
    )
  } finally {
    yield put(actions.togglePageLoading(false))
  }
}

export function* setCalendarDateSaga({ payload }) {
  const { calendarType, selectedDate } = payload
  const state = yield select()

  yield put(actions.updateSelectedCalendarDate(calendarType, selectedDate))
  
  const currentCalendar = yield select(({ scheduling }) => scheduling.calendar)
  const hasDoiCalendar =
    currentCalendar?.install?.calendarType?.toLowerCase() === calendarTypes.doi
  
  if (hasDoiCalendar && calendarType === calendarTypes.install) {
    return
  }

  const calendarItem = getUpdatedCalendarDate(state, payload)

  if (calendarItem) {
    const { calType, updatedDate, clearInstallHighlights } = calendarItem
    yield put(actions.updateSelectedCalendarDate(calType, updatedDate))
    
    if (clearInstallHighlights && calendarType === calendarTypes.prewire) {
      
      const clearedInstallCalendar = currentCalendar.install ? {
        ...currentCalendar.install,
        selected: { date: null, timeSlot: null },
        calendarDates: currentCalendar.install.calendarDates?.map(date => ({
          ...date,
          availableTimeSlots: []
        })) || []
      } : null
      
      const updatedCalendar = {
        ...currentCalendar,
        install: clearedInstallCalendar,
        installRefreshedDates: [],
        refreshedCalendarType: currentCalendar.prewireRefreshedDates?.length
          ? calendarTypes.prewire
          : null
      }
      yield put(actions.setCalendar(updatedCalendar))
    }
  }
}

export function* refreshQuotaWithDateSaga({ payload }) {
  try {
    const { selectedDate, calendarType } = payload
    const { fxbuyflowSessionId } = yield select(({ session }) => session)
    const currentCalendar 
    = yield select(({ scheduling }) => scheduling.calendar)

    yield put(actions.togglePageLoading())
    yield put(actions.clearFormMessage(formErrorIds.timeSlot))
    yield put(actions.clearFormMessage(formErrorIds.abtMessage))
    yield put(actions.clearFormMessage(formErrorIds.genesisIhatMessage))

    const refreshQuotaResponse = yield call(refreshQuotaWithDate, { 
      fxbuyflowSessionId, 
      selectedDate,
      calendarType
    })

    let prewireRefreshedDates = currentCalendar.prewireRefreshedDates || []
    let installRefreshedDates = []
    let refreshedCalendarType = currentCalendar.refreshedCalendarType || null
    let shouldMergeCalendarDates = false

    const hasDoiCalendar =
      refreshQuotaResponse.install?.calendarType.toLowerCase() ===
      calendarTypes.doi
    const prewireCalendar =
      refreshQuotaResponse.prewire?.calendarType.toLowerCase() ===
      calendarTypes.prewire
  
    
    let hasPrewireWithoutAM = false
    if (prewireCalendar) {
      const hasAMTimeslots =
       refreshQuotaResponse.prewire.calendarDates?.some(date =>
        date.availableTimeSlots?.some(slot => slot.id === TIME_SLOT_IDS.AM 
          || slot.time === TIME_SLOT_IDS.AM)
       )
      hasPrewireWithoutAM = !hasAMTimeslots
    }
    
    shouldMergeCalendarDates = hasDoiCalendar || hasPrewireWithoutAM
    
    // Added this condition to set the Partial Automation Prewire calendar when pseudo timeslots are returned
    // from the QATS API. As a result, we will receive only static AM timeslots (CDDD calendar) in the response.
    if(shouldMergeCalendarDates) {    
      const mergedCalendar = 
      mergeCalendarDates(currentCalendar, refreshQuotaResponse)
      
      if (refreshQuotaResponse?.prewire?.calendarDates?.length) {
        let maxPrewireDate = null
        if (mergedCalendar.install?.calendarDates?.length) {
          const lastInstallDate = mergedCalendar.install.calendarDates[
            mergedCalendar.install.calendarDates.length - 1
          ]?.date
          const minGap = mergedCalendar.preWireMinGapBeforeInstallCalendar
          
          if (lastInstallDate && minGap > 0) {
            maxPrewireDate = getPrewireMaxDate(lastInstallDate, minGap)
          }
        }
        
        prewireRefreshedDates = mergedCalendar.prewire.calendarDates
          .filter(date => {
            const hasTimeSlots = date.availableTimeSlots?.length > 0
            const withinRange = !maxPrewireDate 
            || isDateSameOrBefore(date.date, maxPrewireDate)
            return hasTimeSlots && withinRange
          })
          .map(date => date.date)
        refreshedCalendarType = calendarTypes.prewire
      } else if (refreshQuotaResponse?.install?.calendarDates?.length) {
        installRefreshedDates = mergedCalendar.install?.calendarDates
          ?.filter(date => date.availableTimeSlots?.length > 0)
          ?.map(date => date.date) || []
        if (currentCalendar.refreshedCalendarType === calendarTypes.prewire) {
          refreshedCalendarType = calendarTypes.both
        } else {
          refreshedCalendarType = calendarTypes.install
        }
      }

      if (mergedCalendar.installMessageCodes?.includes(GENESIS_IHAT)) {
        yield put(
          actions.setGlobalWarning(
            formErrorIds.genesisIhatMessage,
            mergedCalendar.genesisIhatMessage
          )
        )
      }

      const filteredCalendar = getFilteredCalendar(mergedCalendar)

      if (mergedCalendar.newTimeslotSelectionMessage) {
        yield put(
          actions.setGlobalWarning(
            formErrorIds.timeSlot,
            mergedCalendar.newTimeslotSelectionMessage
          )
        )
      }
    
      const calendarWithHighlights = {
        ...filteredCalendar,
        prewireRefreshedDates,
        installRefreshedDates,
        refreshedCalendarType,
        apiErrorMessage: null
      }
      yield put(actions.setCalendar(calendarWithHighlights))
    } else {
      const filteredCalendar = getFilteredCalendar(refreshQuotaResponse)
      yield put(actions.setCalendar({
        ...filteredCalendar,
        apiErrorMessage: null
      }))
    }
  } catch (error) {
    const errorMsg = getApiErrorMessage(error)    
    const currentCalendar =
     yield select(({ scheduling }) => scheduling.calendar)
    yield put(actions.setCalendar({
      ...currentCalendar,
      apiErrorMessage: errorMsg?.description
    }))
  } finally {
    yield put(actions.togglePageLoading(false))
  }
  
}

function* switchCalendarOptionSaga({ payload }) {
  const { optionType } = payload
  
  const schedulingState = yield select(state => state.scheduling)
  const { validateInstallResponse } = schedulingState

  if (!validateInstallResponse || !validateInstallResponse.calendars) {
    return
  }
      
  setApiPropertiesFromCallbackList(validateInstallResponse, optionType)
  
  const filteredCalendars = filterCalendarsByOption(
    validateInstallResponse.calendars,
    optionType
  )
  
  if (optionType === CALENDAR_TABS.PARTIAL_AUTOMATION) {
    const prewireWithAMSlots = 
    validateInstallResponse.calendars.find(isPrewireWithAMSlots)
    
    if (prewireWithAMSlots && !filteredCalendars.some(isPrewireWithAMSlots)) {
      filteredCalendars.push(prewireWithAMSlots)
    }
  } else if (optionType === CALENDAR_TABS.FULL_AUTOMATION) {
    const prewireWithoutAMSlots = 
    validateInstallResponse.calendars.find(isPrewireWithoutAMSlots)
    
    if (
      prewireWithoutAMSlots &&
      !filteredCalendars.some(isPrewireWithoutAMSlots)
    ) {
      filteredCalendars.push(prewireWithoutAMSlots)
    }
  }
      
  const filteredRawData = {
    ...validateInstallResponse,
    calendars: filteredCalendars
  }
  
  const parsedCalendars = parseSchedulingCalendars(filteredRawData)
  
  yield put(actions.setCalendar(getFilteredCalendar(parsedCalendars)))
  
  yield put(actions.setCalendarOption(optionType))
}

function* rootSaga() {
  yield takeLatest(actions.GET_CALENDAR, getCalendarSaga)
  yield takeLatest(actions.SET_CALENDAR_DATE, setCalendarDateSaga)
  yield takeLatest(actions.REFRESH_QUOTA_WITH_DATE, refreshQuotaWithDateSaga)
  yield takeLatest(actions.SWITCH_CALENDAR_OPTION, switchCalendarOptionSaga)
}

export default rootSaga
